import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditLog } from '@/hooks/useAuditLog';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type ScopeType = 'global' | 'team' | 'personal';

function buildScopeFilter(scope: ScopeType, scopeId?: string, projectId?: string): string {
  const conditions: string[] = [];
  if (projectId) conditions.push(`project_id.eq.${projectId}`);
  conditions.push(`and(scope_type.eq.global,project_id.is.null)`);
  if (scope === 'team' && scopeId) conditions.push(`and(scope_type.eq.team,scope_id.eq.${scopeId},project_id.is.null)`);
  if (scope === 'personal') conditions.push(`and(scope_type.eq.personal,project_id.is.null)`);
  return conditions.join(',');
}

export const useTools = (scope: ScopeType, scopeId?: string, projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['tools', scope, scopeId, projectId],
    enabled: !!user,
    queryFn: async () => {
      const filter = buildScopeFilter(scope, scopeId, projectId);
      const { data, error } = await supabase.from('tools').select('*').or(filter).order('updated_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
};

export const useAllTools = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['tools', 'all'],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
};

export const useCreateTool = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (tool: Omit<TablesInsert<'tools'>, 'owner_id'>) => {
      const { data, error } = await supabase.from('tools').insert({ ...tool, owner_id: user!.id }).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      logAction({ action: 'create_tool', targetType: 'tool', targetId: data.id, metadata: { name: data.name } });
    },
  });
};

export const useUpdateTool = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'tools'> & { id: string }) => {
      const { data, error } = await supabase.from('tools').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      logAction({ action: 'update_tool', targetType: 'tool', targetId: data.id });
    },
  });
};

export const useDeleteTool = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tools').delete().eq('id', id).select('id').single();
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      logAction({ action: 'delete_tool', targetType: 'tool', targetId: id });
    },
  });
};
